import React from 'react';

import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from '@mui/material';
import { has } from 'es-toolkit/compat';

import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import type {
  SelectFieldGroupHeader as GroupHeader,
  SelectFieldOption as Option,
} from 'src/types/components';
import { getFieldError, getFieldWarning, hasFieldError } from 'src/utils/finalForm';

import type { SelectFieldProps } from './interface';

export const SelectField: React.FC<SelectFieldProps> = ({
  input,
  meta,
  fullWidth,
  label,
  options,
  disabled = false,
  warning,
  required = false,
  showErrorOnlyTouched = true,
  showErrorWhenDisabled = true,
  scrollableContent = false,
  size = 'medium',
  renderOption: customRenderOption,
  demoPrivacy = false,
  ...selectProps
}) => {
  const { name, value, onBlur, onChange, multiple } = input;

  const [open, setOpen] = React.useState(false);

  const handleOpen = React.useCallback(() => setOpen(true), []);
  const handleClose = React.useCallback(() => setOpen(false), []);

  const handleChange = React.useCallback(
    (event: SelectChangeEvent) => {
      onChange(event.target.value);

      // Close <Select>'s pop-up when only one option is present and it's been clicked
      // By default <Select> with multiple=true won't close its pop-up to allow for multiple options selection
      if (multiple && options.length <= 1) {
        handleClose();
      }
    },
    [handleClose, multiple, onChange, options.length],
  );

  const labelId = React.useId();
  const error = hasFieldError(meta, showErrorOnlyTouched);
  const showError = showErrorWhenDisabled || !disabled;
  const helperText = showError
    ? getFieldError(meta, showErrorOnlyTouched) || getFieldWarning(value, meta, warning, disabled)
    : '';

  const renderOption = (option: Option | GroupHeader) => {
    if (isGroupHeader(option)) {
      return <ListSubheader key={option.groupName}>{option.groupName}</ListSubheader>;
    }

    return (
      <MenuItem
        sx={{
          whiteSpace: 'normal',
        }}
        key={option.value}
        value={option.value}
      >
        {demoPrivacy ? <DemoPrivacyText>{option.label}</DemoPrivacyText> : option.label}
      </MenuItem>
    );
  };

  return (
    <FormControl
      variant="outlined"
      error={showError ? error : false}
      fullWidth={fullWidth}
      required={required}
      size={size}
    >
      {label && (
        <InputLabel id={labelId} disabled={disabled}>
          {label}
        </InputLabel>
      )}

      <Select
        labelId={labelId}
        name={name}
        value={multiple ? value || [] : value}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleChange}
        onBlur={onBlur}
        label={label}
        multiple={multiple}
        disabled={disabled}
        {...selectProps}
        MenuProps={{
          PaperProps: {
            sx: {
              maxWidth: fullWidth ? '30%' : undefined,
              maxHeight: scrollableContent ? '25%' : undefined,
            },
          },
        }}
      >
        {options.length === 0 && <Box textAlign="center">No options</Box>}
        {options.map(option =>
          customRenderOption ? customRenderOption(option) : renderOption(option),
        )}
      </Select>

      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

const isGroupHeader = (arg: Option | GroupHeader): arg is GroupHeader => {
  return has(arg, 'groupName');
};
